// ==============================
// Mobile card view for the Class Management table
// ==============================
// On phones the wide class table becomes a list of cards (key fields + the same
// Manage / Edit / Delete actions). Used behind useIsMobile() in ClassTable; the
// desktop/tablet table is unchanged. Renders a flat list — the "Group by Week"
// grouping is handled by the caller (week headers wrap these cards).

import { ReactNode } from 'react';
import { Delete, Edit, ManageAccounts } from '@mui/icons-material';
import {
  Box,
  Card,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { Class, ClassStatus } from '../types/ClassTable.type';
import { serviceTypeColors } from '../utils/colorHandler';
import ColorChip from './common/ColorChip';
import SofLevelChip from './common/SofLevelChip';
import { EmptyValue } from './common/EmptyValue';
import { useTranslation } from '../i18n';

// One label/value row inside a card.
const Field = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box
    sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: 'right', minWidth: 0 }}>{children}</Box>
  </Box>
);

type ClassCardListProps = {
  classes: Class[];
  onManage: (record: Class) => void;
  onEdit: (record: Class) => void;
  onDelete: (record: Class) => void;
};

const ClassCardList = ({
  classes,
  onManage,
  onEdit,
  onDelete,
}: ClassCardListProps) => {
  const { t } = useTranslation();

  const statusDisplay = (status: ClassStatus) => {
    if (status === ClassStatus.ACTIVE)
      return { label: t('class.statusActive'), color: '#4caf50' };
    if (status === ClassStatus.CLOSED)
      return { label: t('class.statusClosed'), color: '#f44336' };
    return { label: t('class.statusCancelled'), color: '#ff9800' };
  };

  if (classes.length === 0) {
    return (
      <Box py={6} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          {t('class.noClassesFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {classes.map((record) => {
        const status = statusDisplay(record.status);
        return (
          <Card key={record.id} variant="outlined" sx={{ p: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {record.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('class.colId')} {record.id}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexShrink: 0 }}>
                <Tooltip title={t('class.tooltipManageClass')}>
                  <IconButton
                    color="primary"
                    aria-label="manage class"
                    onClick={() => onManage(record)}
                  >
                    <ManageAccounts />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('class.tooltipEditClass')}>
                  <IconButton
                    color="primary"
                    aria-label="edit class"
                    onClick={() => onEdit(record)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('class.tooltipDeleteClass')}>
                  <IconButton
                    color="error"
                    aria-label="delete class"
                    onClick={() => onDelete(record)}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Field label={t('class.colDate')}>
              {record.classDate ? (
                new Date(record.classDate).toLocaleDateString('en-GB')
              ) : (
                <EmptyValue label="---" />
              )}
            </Field>
            <Field label={t('class.colTime')}>
              {`${record.startTime || '---'} - ${record.endTime || '---'}`}
            </Field>
            <Field label={t('class.colClassType')}>
              <ColorChip label={record.subtypeName} palette={serviceTypeColors} />
            </Field>
            <Field label={t('class.colLevel')}>
              <SofLevelChip
                level={record.level}
                serviceType={record.subtypeName}
              />
            </Field>
            <Field label={t('class.colCoachTeacher')}>
              {record.coachTeachers?.join(', ') || <EmptyValue />}
            </Field>
            <Field label={t('class.fieldStatus')}>
              <Typography
                variant="body2"
                sx={{ color: status.color, fontWeight: 'bold' }}
              >
                {status.label}
              </Typography>
            </Field>
            <Field label={t('class.fieldNote')}>
              {record.note || <EmptyValue />}
            </Field>
          </Card>
        );
      })}
    </Stack>
  );
};

export default ClassCardList;
